import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, Package, Building2, TrendingUp } from 'lucide-react';

import { getAnalytics, type AnalyticsPeriod, type FunnelStatus } from '@/services/analytics';
import { PageHeader, Spinner, EmptyState } from '@/components/fields';
import { rub } from '@/lib/format';

const PERIODS: { id: AnalyticsPeriod; label: string }[] = [
  { id: '7d', label: '7 дней' },
  { id: '30d', label: '30 дней' },
  { id: '90d', label: '90 дней' },
  { id: 'all', label: 'Всё' },
];

const FUNNEL: { key: FunnelStatus; label: string; cls: string }[] = [
  { key: 'NEW', label: 'Новые', cls: 'bg-sky-500' },
  { key: 'CONFIRMED', label: 'Подтверждено', cls: 'bg-indigo-500' },
  { key: 'SHIPPED', label: 'Отгружено', cls: 'bg-violet-500' },
  { key: 'DELIVERED', label: 'Доставлено', cls: 'bg-emerald-500' },
];

const dayLabel = (iso: string) => {
  const [, m, d] = iso.split('-');
  return `${d}.${m}`;
};

export default function Analytics() {
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d');
  const [custom, setCustom] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['analytics', custom ? `r:${from}|${to}` : `p:${period}`],
    queryFn: () =>
      getAnalytics(
        custom ? { period, from: from || undefined, to: to || undefined } : { period },
      ),
  });

  function pickPeriod(p: AnalyticsPeriod) {
    setCustom(false);
    setPeriod(p);
  }

  function resetRange() {
    setCustom(false);
    setFrom('');
    setTo('');
  }

  return (
    <div>
      <PageHeader
        title="Аналитика"
        subtitle="Оборот, спрос и повторные заказы по заведениям"
        actions={
          <div className="inline-flex rounded-lg border border-line bg-white p-0.5">
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={() => pickPeriod(p.id)}
                className={`px-3 py-1.5 text-sm rounded-md transition ${
                  period === p.id && !custom
                    ? 'bg-orange text-white font-medium'
                    : 'text-ink/60 hover:bg-paper'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        }
      />

      {/* Произвольный диапазон дат */}
      <div className="flex flex-wrap items-center gap-2 mb-5 -mt-2">
        <span className="text-xs text-ink/40">или диапазон:</span>
        <input
          type="date"
          value={from}
          max={to || undefined}
          onChange={(e) => {
            setFrom(e.target.value);
            setCustom(true);
          }}
          className="input !w-auto !py-1.5 text-sm"
        />
        <span className="text-ink/30">—</span>
        <input
          type="date"
          value={to}
          min={from || undefined}
          onChange={(e) => {
            setTo(e.target.value);
            setCustom(true);
          }}
          className="input !w-auto !py-1.5 text-sm"
        />
        {custom && (from || to) && (
          <button onClick={resetRange} className="text-xs text-orange hover:underline">
            сбросить
          </button>
        )}
      </div>

      {isLoading ? (
        <Spinner label="Считаем аналитику…" />
      ) : !data || data.kpi.ordersCount === 0 ? (
        <EmptyState
          icon={<BarChart3 size={40} strokeWidth={1.4} />}
          title="Нет данных за период"
          description="Когда заведения начнут заказывать, здесь появятся оборот, топ-товары и воронка."
        />
      ) : (
        <div className="space-y-5">
          {/* KPI */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Оборот (живые заказы)" value={rub(data.kpi.turnover)} accent="orange" />
            <Kpi label="Доставлено" value={rub(data.kpi.revenueDelivered)} accent="emerald" />
            <Kpi label="Средний чек" value={rub(data.kpi.avgCheck)} />
            <Kpi label="Заказов" value={String(data.kpi.ordersCount)} />
            <Kpi label="Заведений" value={String(data.kpi.uniqueEstablishments)} />
            <Kpi
              label="Повторные заказы"
              value={`${data.kpi.repeatRate}%`}
              hint="заведений с ≥2 заказами"
            />
            <Kpi label="Доставлено заказов" value={String(data.kpi.deliveredCount)} />
            <Kpi
              label="Конверсия в доставку"
              value={`${data.kpi.ordersCount ? Math.round((data.kpi.deliveredCount / data.kpi.ordersCount) * 100) : 0}%`}
            />
          </div>

          {/* График оборота по дням */}
          <RevenueChart series={data.series} />

          <div className="grid lg:grid-cols-2 gap-5">
            {/* Воронка */}
            <div className="card p-5">
              <h3 className="font-semibold text-ink mb-4">Статусы заказов</h3>
              <Funnel funnel={data.funnel} />
            </div>

            {/* Топ-товары */}
            <div className="card p-5">
              <h3 className="font-semibold text-ink mb-4 flex items-center gap-2">
                <Package size={16} className="text-ink/40" /> Топ-товары
              </h3>
              {data.topProducts.length === 0 ? (
                <p className="text-sm text-ink/40">Нет доставленных позиций.</p>
              ) : (
                <ol className="space-y-2.5">
                  {data.topProducts.map((p, i) => (
                    <li key={p.name + i} className="flex items-center gap-3">
                      <span className="w-5 text-xs text-ink/35 font-medium shrink-0">{i + 1}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-ink truncate">{p.name}</div>
                        <div className="text-xs text-ink/40">
                          {p.quantity} {p.unit}
                        </div>
                      </div>
                      <div className="text-sm font-medium text-ink shrink-0">{rub(p.sum)}</div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>

          {/* Топ-заведения */}
          <div className="card p-5">
            <h3 className="font-semibold text-ink mb-4 flex items-center gap-2">
              <Building2 size={16} className="text-ink/40" /> Топ-заведения по обороту
            </h3>
            {data.topEstablishments.length === 0 ? (
              <p className="text-sm text-ink/40">Нет заказов.</p>
            ) : (
              <div className="divide-y divide-line">
                {data.topEstablishments.map((e, i) => (
                  <div key={e.name + i} className="py-2.5 flex items-center gap-3">
                    <span className="w-5 text-xs text-ink/35 font-medium shrink-0">{i + 1}</span>
                    <div className="min-w-0 flex-1 text-sm text-ink truncate">{e.name}</div>
                    <div className="text-xs text-ink/40 shrink-0">{e.ordersCount} зак.</div>
                    <div className="text-sm font-medium text-ink shrink-0 w-24 text-right">
                      {rub(e.sum)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: 'orange' | 'emerald';
}) {
  const color =
    accent === 'orange' ? 'text-orange' : accent === 'emerald' ? 'text-emerald-600' : 'text-ink';
  return (
    <div className="card p-4">
      <div className="text-xs text-ink/45">{label}</div>
      <div className={`text-xl font-semibold mt-1 ${color}`}>{value}</div>
      {hint && <div className="text-[11px] text-ink/35 mt-0.5">{hint}</div>}
    </div>
  );
}

function RevenueChart({ series }: { series: { date: string; orders: number; turnover: number }[] }) {
  const max = Math.max(1, ...series.map((s) => s.turnover));
  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-ink flex items-center gap-2">
          <TrendingUp size={16} className="text-ink/40" /> Оборот по дням
        </h3>
        <span className="text-xs text-ink/40">макс. {rub(max)}</span>
      </div>
      {series.length === 0 ? (
        <p className="text-sm text-ink/40 py-6 text-center">Нет данных за период.</p>
      ) : (
        <div className="overflow-x-auto">
          <div className="flex items-end gap-1 h-40 min-w-full">
            {series.map((s) => (
              <div
                key={s.date}
                className="flex-1 min-w-[6px] h-full group relative flex flex-col justify-end"
                title={`${dayLabel(s.date)} · ${rub(s.turnover)} · ${s.orders} зак.`}
              >
                <div
                  className="w-full rounded-t bg-orange/80 hover:bg-orange transition-colors"
                  style={{ height: `${Math.max(2, (s.turnover / max) * 100)}%` }}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-[10px] text-ink/35">
            <span>{dayLabel(series[0].date)}</span>
            {series.length > 1 && <span>{dayLabel(series[series.length - 1].date)}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function Funnel({ funnel }: { funnel: Record<FunnelStatus, number> }) {
  const max = Math.max(1, ...FUNNEL.map((f) => funnel[f.key] ?? 0));
  return (
    <div className="space-y-2.5">
      {FUNNEL.map((f) => {
        const n = funnel[f.key] ?? 0;
        return (
          <div key={f.key} className="flex items-center gap-3">
            <div className="w-28 text-xs text-ink/55 shrink-0">{f.label}</div>
            <div className="flex-1 h-6 rounded bg-paper overflow-hidden">
              <div
                className={`h-full ${f.cls} rounded`}
                style={{ width: `${Math.max(n ? 6 : 0, (n / max) * 100)}%` }}
              />
            </div>
            <div className="w-8 text-right text-sm font-medium text-ink shrink-0">{n}</div>
          </div>
        );
      })}
      <div className="flex gap-2 pt-1.5">
        <span className="badge bg-red-50 text-red-600">Отклонено: {funnel.REJECTED ?? 0}</span>
        <span className="badge bg-ink/5 text-ink/50">Отменено: {funnel.CANCELLED ?? 0}</span>
      </div>
    </div>
  );
}
